import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Plus, MapPin, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FAQS = [
  { q: "Is this therapy?", a: "No. It is not therapy, medical care, or an escort service. It is private 1:1 guidance — practical and personal. If clinical support is what you need, I'll gladly point you toward a queer-affirming therapist." },
  { q: "Do I need to be 21+?", a: "Yes. Rebel&Co is for adults only. Age is confirmed at account creation." },
  { q: "How do I see pricing and booking?", a: "Create a free account to view each experience's details, pricing, and open booking windows. There is no paid subscription — the account itself is free." },
];

export default function Home() {
  const [cities, setCities] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => { api.get("/cities").then(r => setCities(r.data)).catch(() => {}); }, []);
  const vegas = cities.find(c => c.city_id === "lv");

  return (
    <div data-testid="home-page" className="font-body">
      {/* ========== HERO ========== */}
      <section id="hero" className="relative min-h-[100vh] flex items-center overflow-hidden">
        <div className="rc-hero-bg absolute inset-0 z-0" aria-hidden="true" />
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 pt-32 pb-20">
          <div className="max-w-2xl">
            <span data-testid="hero-eyebrow" className="rc-eyebrow fade-up">Private 1:1 guidance</span>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold mt-6 fade-up"
                style={{ letterSpacing: "-0.03em", lineHeight: "1.05" }}>
              Move with <span className="text-[#2d7cf6]">confidence.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[color:var(--rc-text-muted)] leading-relaxed max-w-xl fade-up">
              Get clear on what you want. Build confidence in how you show up.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up">
              <Link to="/experiences" data-testid="hero-book-cta" className="rc-btn rc-btn-blue">
                Book Your Session <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#services" data-testid="hero-explore-cta" className="rc-btn rc-btn-outline-new">
                See Experiences
              </a>
            </div>
            <p className="mt-6 text-xs text-[color:var(--rc-text-dim)] font-body">
              Private · 1:1 · Judgment-free. Curiosity is enough.
            </p>
          </div>
        </div>
      </section>

      {/* ========== WORK WITH ME ========== */}
      <section id="services" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">Work with me</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3" style={{ letterSpacing: "-0.02em" }}>
            Three ways in.
          </h2>
          <p className="mt-3 text-base text-[color:var(--rc-text-muted)] max-w-[55ch] leading-relaxed">
            Private 1:1 experiences — pick the one that matches where you are.
          </p>

          <ServiceCards />
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {/* ========== ABOUT / APPROACH ========== */}
      <section id="about-preview" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">The approach</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3" style={{ letterSpacing: "-0.02em" }}>
            Clarity to <span className="text-[#2d7cf6]">move forward.</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-10 lg:gap-16 items-start">
            <div className="md:col-span-3">
              <p className="text-base sm:text-lg text-[color:var(--rc-text-muted)] leading-relaxed">
                A private space for gay, bi, and queer men to get clear on what you want and build confidence in how you show up.
              </p>
              <p className="mt-5 text-base text-[color:var(--rc-text-muted)] leading-relaxed">
                Sessions are 1:1 and shaped around you. Pick a city, pick an experience, come curious.
              </p>
              <div className="mt-8">
                <Link to="/about" data-testid="about-preview-link" className="rc-btn rc-btn-outline-new">
                  More about the practice <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-3 gap-6 md:pl-8 md:border-l border-[color:var(--rc-divider)]" data-testid="about-preview-stats">
              <div>
                <div className="font-display text-2xl sm:text-3xl font-semibold text-[#2d7cf6]">1:1</div>
                <div className="mt-2 text-[11px] text-[color:var(--rc-text-muted)] uppercase tracking-[0.1em]">Private</div>
              </div>
              <div>
                <div className="font-display text-2xl sm:text-3xl font-semibold text-[#2d7cf6]">21+</div>
                <div className="mt-2 text-[11px] text-[color:var(--rc-text-muted)] uppercase tracking-[0.1em]">Adults only</div>
              </div>
              <div>
                <div className="font-display text-2xl sm:text-3xl font-semibold text-[#2d7cf6]">0</div>
                <div className="mt-2 text-[11px] text-[color:var(--rc-text-muted)] uppercase tracking-[0.1em]">Judgment</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {/* ========== VEGAS AVAILABILITY (compact) ========== */}
      <section id="availability" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">On the road</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3" style={{ letterSpacing: "-0.02em" }}>
            First city — <span className="text-[#2d7cf6]">Las Vegas.</span>
          </h2>
          {vegas ? (
            <div data-testid="vegas-availability-card" className="mt-8 rc-service-card max-w-xl">
              <div className="flex items-center gap-2 text-[color:var(--rc-text-muted)]">
                <MapPin className="w-4 h-4 text-[#2d7cf6]" />
                <span className="text-xs uppercase tracking-[0.1em] font-semibold">{vegas.name}</span>
              </div>
              <div data-testid="vegas-dates" className="mt-4 font-display text-2xl sm:text-3xl font-bold text-[color:var(--rc-text)]">{vegas.dates}</div>
              {vegas.timezone && (
                <div className="mt-1 text-xs text-[color:var(--rc-text-muted)] uppercase tracking-[0.1em]">
                  Timezone · {vegas.timezone.replace("_", " ")}
                </div>
              )}
              <p className="mt-6 text-sm text-[color:var(--rc-text-muted)] leading-relaxed">
                Exact session hours and inclusions are being finalized. Request availability and I'll reach out privately when they open — no payment, no commitment.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/cities" data-testid="vegas-request-cta" className="rc-btn rc-btn-blue">
                  Request Availability <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/cities" data-testid="vegas-see-cities" className="rc-btn rc-btn-outline-new">
                  See Cities
                </Link>
              </div>
            </div>
          ) : (
            <div data-testid="availability-loading" className="mt-8 text-sm text-[color:var(--rc-text-dim)]">
              Loading availability…
            </div>
          )}
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {/* ========== FAQ ========== */}
      <section id="faq" className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">FAQ</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3" style={{ letterSpacing: "-0.02em" }}>
            Questions you might have.
          </h2>

          <div className="mt-10 max-w-[640px]" data-testid="faq-list">
            {FAQS.map((item, idx) => {
              const open = openFaq === idx;
              return (
                <div key={item.q} className="border-b border-[color:var(--rc-divider)]">
                  <button
                    type="button"
                    data-testid={`faq-q-${idx}`}
                    aria-expanded={open}
                    aria-controls={`faq-a-${idx}`}
                    onClick={() => setOpenFaq(open ? null : idx)}
                    className="w-full flex justify-between items-center gap-4 text-left py-5 font-body font-medium text-base sm:text-lg text-[color:var(--rc-text)] hover:text-[#5a9aff] transition-colors"
                    style={{ minHeight: 44 }}
                  >
                    <span>{item.q}</span>
                    <Plus
                      className="w-5 h-5 flex-shrink-0 text-[color:var(--rc-text-muted)] transition-transform duration-300"
                      style={{ transform: open ? "rotate(45deg)" : "rotate(0)" }}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id={`faq-a-${idx}`}
                    role="region"
                    aria-hidden={!open}
                    className="overflow-hidden transition-[max-height,padding] duration-400"
                    style={{ maxHeight: open ? 400 : 0 }}
                  >
                    <p className="pb-5 text-sm sm:text-base text-[color:var(--rc-text-muted)] leading-relaxed">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {/* ========== BOOKING CTA ========== */}
      <section id="book" className="py-20 sm:py-28 bg-[color:var(--rc-surface-1)] border-t border-b border-[color:var(--rc-border)]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <span className="rc-eyebrow">Ready?</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3 max-w-[22ch] mx-auto" style={{ letterSpacing: "-0.02em" }}>
            Choose your experience.
          </h2>
          <p className="mt-5 text-base text-[color:var(--rc-text-muted)] max-w-[55ch] mx-auto leading-relaxed">
            Bring your questions. Explore what comes next.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/experiences" data-testid="closing-book-cta" className="rc-btn rc-btn-blue rc-btn-lg">
              Book Your Session <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" data-testid="closing-contact-cta" className="rc-btn rc-btn-outline-new rc-btn-lg">
              Start a Private Conversation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCards() {
  const { user } = useAuth();
  const [pkgs, setPkgs] = useState([]);
  const [full, setFull] = useState([]);
  useEffect(() => {
    api.get("/packages").then(r => setPkgs(r.data)).catch(() => setPkgs([]));
    if (user) api.get("/packages/full").then(r => setFull(r.data)).catch(() => setFull([]));
  }, [user]);
  if (!pkgs.length) {
    return <div data-testid="services-loading" className="mt-10 text-sm text-[color:var(--rc-text-dim)]">Loading experiences…</div>;
  }
  const byKey = Object.fromEntries(full.map(p => [p.lookup_key, p]));
  return (
    <>
      {!user && (
        <p data-testid="services-public-hint" className="mt-5 text-sm text-[color:var(--rc-text-muted)] max-w-[55ch] leading-relaxed">
          Create a free account for details, pricing and booking.
        </p>
      )}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="services-grid">
        {pkgs.map(p => {
          const detail = byKey[p.lookup_key];
          return (
            <div key={p.lookup_key} data-testid={`service-card-${p.lookup_key}`}
                 className={`rc-service-card ${p.signature ? "featured" : ""}`}>
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#2d7cf6] mb-3">{p.subtitle}</span>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-[color:var(--rc-text)] mb-3">{p.name}</h3>
              <p className="text-sm sm:text-base text-[color:var(--rc-text-muted)] leading-relaxed flex-1 mb-6">{p.tagline}</p>

              {detail ? (
                <>
                  <div className="font-display text-xl font-semibold text-[color:var(--rc-text)] mb-4">
                    ${(detail.amount / 100).toLocaleString()}{" "}
                    <span className="text-sm font-normal text-[color:var(--rc-text-muted)] font-body">/ {detail.duration}</span>
                  </div>
                  <ul className="list-none p-0 m-0 mb-6 space-y-2">
                    {detail.features?.map(f => (
                      <li key={f} className="text-sm text-[color:var(--rc-text-muted)] py-2 border-b border-[color:var(--rc-divider)] last:border-b-0 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2d7cf6] flex-shrink-0" aria-hidden="true" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/dashboard/book?pkg=${p.lookup_key}`} state={{ pkg: p.lookup_key }} data-testid={`book-${p.lookup_key}`}
                        className={`rc-btn w-full ${p.signature ? "rc-btn-blue" : "rc-btn-outline-new"}`}>
                    Book {p.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              ) : (
                <div className="mt-auto">
                  <div data-testid={`home-gate-${p.lookup_key}`} className="flex items-center gap-2 text-xs text-[color:var(--rc-text-muted)] uppercase tracking-[0.12em] mb-4">
                    <Lock className="w-3 h-3" /> Members only
                  </div>
                  <Link
                    to={`/signup?pkg=${p.lookup_key}`}
                    state={{ from: `/dashboard/book?pkg=${p.lookup_key}` }}
                    data-testid={`home-join-${p.lookup_key}`}
                    className={`rc-btn w-full ${p.signature ? "rc-btn-blue" : "rc-btn-outline-new"}`}
                  >
                    Join the Rebel List <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/login?pkg=${p.lookup_key}`}
                    state={{ from: `/dashboard/book?pkg=${p.lookup_key}` }}
                    data-testid={`home-signin-${p.lookup_key}`}
                    className="mt-3 block text-center text-xs text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)] underline-editorial"
                  >
                    Already a member? Sign in
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
